/**
 * Was sich das Geraet je Benutzer merkt: das letzte Ausbuchen-Ziel und die
 * letzten vier Ziele (ADR 0013 §14).
 *
 * Im `localStorage`, nach `benutzer_id` getrennt -- an der Einsatzstelle geht
 * das Handy durch viele Haende, und jeder hat seine eigenen Wege. Nicht auf dem
 * Server: Das muss offline gehen und ist Bequemlichkeit, kein Bestand. Geht
 * der Speicher verloren, beginnt es eben wieder bei „Ohne festen Platz“.
 */

import type { Ziel } from "@thw/core-domain";

/** So viele Ziele stehen als Chips in „Wohin?“. */
export const LETZTE_ZIELE = 4;

export interface GemerkteZiele {
  /** Vorbelegung beim Ausbuchen; `null` heisst: das Standardziel. */
  ausbuchen: Ziel | null;
  /** Neueste zuerst, ohne Doppelte. */
  letzte: Ziel[];
}

const LEER: GemerkteZiele = { ausbuchen: null, letzte: [] };

function schluessel(benutzer_id: string): string {
  return `thw-ziele-${benutzer_id}`;
}

function istZiel(wert: unknown): wert is Ziel {
  if (typeof wert !== "object" || wert === null) return false;
  const z = wert as Record<string, unknown>;
  return (z["art"] === "lagerort" || z["art"] === "einsatzstelle") && typeof z["id"] === "string";
}

export function zieleLesen(benutzer_id: string): GemerkteZiele {
  try {
    const roh = globalThis.localStorage.getItem(schluessel(benutzer_id));
    if (roh === null) return LEER;
    const gelesen = JSON.parse(roh) as Partial<Record<keyof GemerkteZiele, unknown>>;
    return {
      ausbuchen: istZiel(gelesen.ausbuchen) ? gelesen.ausbuchen : null,
      letzte: Array.isArray(gelesen.letzte) ? gelesen.letzte.filter(istZiel) : [],
    };
  } catch {
    // Privates Fenster oder gesperrter Speicher: dann ohne Gedaechtnis.
    return LEER;
  }
}

function schreiben(benutzer_id: string, ziele: GemerkteZiele): void {
  try {
    globalThis.localStorage.setItem(schluessel(benutzer_id), JSON.stringify(ziele));
  } catch {
    // Wirkt dann nur bis zum Neuladen nicht -- kein Grund, eine Buchung aufzuhalten.
  }
}

export function gleichesZiel(a: Ziel, b: Ziel): boolean {
  return a.art === b.art && a.id === b.id;
}

/** Nach vorn in die letzten Ziele; das aelteste faellt heraus. */
export function vorn(liste: readonly Ziel[], ziel: Ziel): Ziel[] {
  return [ziel, ...liste.filter((z) => !gleichesZiel(z, ziel))].slice(0, LETZTE_ZIELE);
}

/** Ein gewaehltes Ziel merken. `ausbuchen`: auch als Vorbelegung fuers Ausbuchen. */
export function zielMerken(benutzer_id: string, ziel: Ziel, ausbuchen = false): void {
  const bisher = zieleLesen(benutzer_id);
  schreiben(benutzer_id, {
    ausbuchen: ausbuchen ? ziel : bisher.ausbuchen,
    letzte: vorn(bisher.letzte, ziel),
  });
}
